import { ArrowsPointingOutIcon, PlusIcon } from '@heroicons/react/24/outline';
import { ReactFlowProvider } from '@xyflow/react';
import { CopyIcon, PlayIcon, SaveIcon } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DuplicateQueryModal,
  UnsavedChangesModal,
} from '@/sections/insights/queryBuilder/components/Modals';
import { QueryVisualizer } from '@/sections/insights/queryBuilder/components/QueryVisualizer';
import { useQueryOperations } from '@/sections/insights/queryBuilder/hooks/useQueryOperations';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';

import { UserSelectorDropdown } from './UserSelectorDropdown';

/**
 * QueryEditor - Query editing interface
 *
 * Provides an interactive graph-based query editor with:
 * - Visual query builder with draggable nodes
 * - Auto Arrange button for dagre layout
 * - Execute and Save actions for query management
 * - Error handling for invalid states
 *
 * Triggered by:
 * - QueryMenu "Edit Query" action (custom queries only)
 * - QueryMenu "Duplicate Query" action (all queries)
 *
 * Header is rendered by parent QueryBuilderWorkstation
 */
export const QueryEditor = () => {
  const { queryState, isGlobal, getSavedQuery } = useQueryBuilder();
  const {
    handleExecute,
    handleSave,
    isSaving,
    pendingOverwrite,
    confirmOverwrite,
    cancelOverwrite,
  } = useQueryOperations();
  const [isPlacementMode, setIsPlacementMode] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true);
  };

  const layoutGraphRef = useRef<(() => void) | null>(null);

  const handleAutoArrange = useCallback(() => {
    layoutGraphRef.current?.();
  }, []);

  // Error handling for missing query
  if (!queryState) {
    toast.error('No query selected');
    return null;
  }

  return (
    <>
      <div className="relative flex h-full flex-col">
        {/* Floating action panel - top right */}
        <div
          className="border-chariot-stroke bg-chariot-background-primary absolute top-4 right-4 z-10 flex items-center gap-2 rounded-md border px-3 py-2"
          data-testid="query-editor-actions-panel"
          role="group"
          aria-label="Query editor actions"
        >
          {/* Add Node - Placement mode toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPlacementMode(!isPlacementMode)}
            className="rounded-full"
            data-testid="query-editor-add-node-btn"
            aria-label={isPlacementMode ? 'Cancel node placement' : 'Add node'}
          ><PlusIcon className="size-4" />{isPlacementMode ? 'Cancel' : 'Add Node'}</Button>

          {/* Separator */}
          <div
            className="bg-chariot-stroke h-6 w-px"
            role="separator"
            aria-orientation="vertical"
          />

          {/* Auto Arrange - NEW */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoArrange}
            className="rounded-full"
            data-testid="query-editor-auto-arrange-btn"
            aria-label="Auto-arrange graph nodes"
          ><ArrowsPointingOutIcon className="size-4" />Auto Arrange</Button>

          {/* Separator */}
          <div
            className="bg-chariot-stroke h-6 w-px"
            role="separator"
            aria-orientation="vertical"
          />

          {/* NEW: Users dropdown (conditional) - Component handles everything internally */}
          {isGlobal && (
            <>
              <UserSelectorDropdown />
              <div
                className="bg-chariot-stroke h-6 w-px"
                role="separator"
                aria-orientation="vertical"
              />
            </>
          )}

          {/* Execute - Secondary action (stub) */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExecute}
            className="rounded-full"
            data-testid="query-editor-execute-btn"
            aria-label="Execute query"
          ><PlayIcon className="size-4" />Execute</Button>

          {/* Separator */}
          <div
            className="bg-chariot-stroke h-6 w-px"
            role="separator"
            aria-orientation="vertical"
          />

          {/* Save - Primary action with loading state */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            className="rounded-full bg-chariot-brand-primary hover:bg-chariot-brand-secondary text-white"
            data-testid="query-editor-save-btn"
            aria-label="Save query"
            disabled={isSaving}
          ><SaveIcon className="size-4" />{isSaving ? 'Saving...' : 'Save'}</Button>

          {/* Separator */}
          <div
            className="bg-chariot-stroke h-6 w-px"
            role="separator"
            aria-orientation="vertical"
          />

          {/* Duplicate - Primary action */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDuplicate}
            className="rounded-full bg-chariot-brand-primary hover:bg-chariot-brand-secondary text-white"
            data-testid="query-editor-duplicate-btn"
            aria-label="Duplicate query"
          ><CopyIcon className="size-4" />Duplicate</Button>
        </div>

        <ReactFlowProvider>
          <QueryVisualizer
            editable={true}
            isPlacementMode={isPlacementMode}
            onNodePlaced={() => setIsPlacementMode(false)}
            layoutGraphRef={layoutGraphRef}
          />
        </ReactFlowProvider>
      </div>

      {getSavedQuery() && (
        <DuplicateQueryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          originalQuery={getSavedQuery()!}
        />
      )}

      <UnsavedChangesModal
        isOpen={!!pendingOverwrite}
        onConfirm={() => {
          confirmOverwrite();
        }}
        onCancel={cancelOverwrite}
        title="Overwrite existing query?"
        message="A query with this ID already exists. Do you want to overwrite it?"
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
      />
    </>
  );
};
